import {
  createSlice,
  type PayloadAction,
  type Reducer,
} from '@reduxjs/toolkit';
import {
  DEFAULT_PAWN_COLOUR,
  readPawnColour,
  writePawnColour,
  type PawnColour,
} from '@root/lib/pawnColour';
import type { RootState } from '@root/store/store';
import type { LudoRollResult } from '@root/store/types';

export const JOURNEY_SLICE_KEY = 'journey';

export interface JourneySliceState {
  colour: PawnColour;
  // Intro only plays once, at the start of a run.
  introPlayed: boolean;
  // Each roll is its own round, so the id says whether we've played it.
  animatedRoundId: string | null;
  // Pawn is moving. The roll button stays locked until the scene says done.
  rollInFlight: boolean;
  // Sent, waiting on the server. rollInFlight starts when the answer lands.
  rollSubmitted: boolean;
  // Which run the rolls below belong to.
  journeyId: string | null;
  // The frame carries only the latest roll, so the strip keeps its own list.
  rolls: LudoRollResult[];
}

export const initialState: JourneySliceState = {
  colour: DEFAULT_PAWN_COLOUR,
  introPlayed: false,
  animatedRoundId: null,
  rollInFlight: false,
  rollSubmitted: false,
  journeyId: null,
  rolls: [],
};

const journeySlice = createSlice({
  name: JOURNEY_SLICE_KEY,
  initialState,
  reducers: {
    // Restores the player's last pick; falls back to the default.
    loadPawnColour: (state) => {
      state.colour = readPawnColour();
    },
    setPawnColour: (state, action: PayloadAction<PawnColour>) => {
      state.colour = action.payload;
      writePawnColour(action.payload);
    },
    setIntroPlayed: (state, action: PayloadAction<boolean>) => {
      state.introPlayed = action.payload;
    },
    setAnimatedRoundId: (state, action: PayloadAction<string | null>) => {
      state.animatedRoundId = action.payload;
    },
    setRollInFlight: (state, action: PayloadAction<boolean>) => {
      state.rollInFlight = action.payload;
    },
    setRollSubmitted: (state, action: PayloadAction<boolean>) => {
      state.rollSubmitted = action.payload;
    },
    // A new run starts the list over.
    recordRoll: (
      state,
      action: PayloadAction<{ journeyId: string; roll: LudoRollResult }>
    ) => {
      if (state.journeyId !== action.payload.journeyId) {
        state.journeyId = action.payload.journeyId;
        state.rolls = [];
      }
      state.rolls.push(action.payload.roll);
    },
    // Keep the colour. It is the player's pick, not the run's.
    resetJourney: (state) => {
      state.introPlayed = false;
      state.animatedRoundId = null;
      state.rollInFlight = false;
      state.rollSubmitted = false;
      state.journeyId = null;
      state.rolls = [];
    },
  },
});

export const journeySelector = (state: RootState): JourneySliceState =>
  state[JOURNEY_SLICE_KEY];

export const journeyActions = journeySlice.actions;
export default journeySlice.reducer as Reducer<JourneySliceState>;
